import React, { FunctionComponent } from 'react'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import Gradient from '@sport1/react-elements/Gradient'
import withTheme from '@sport1/react-elements/withTheme'
import CaptionFont from '../../../Theme/Fonts/Caption'
import Label10Font from '../../../Theme/Fonts/Label10'
import Image from '../../../BaseComponents/Image'
import { TestID } from '../../../utils/PropTypesDefinitions'
import { ThemeProps } from '../../../Theme/ThemeComponentTypes'
import lightTheme from '../../../themes/LightTheme'

export type DataPoint = {
    /**
     * URL to competitor or competition logo
     * */
    logoUrl: string
    /**
     * statistical value to show as label below logo
     * should be with standing for in-between competitor, e.g. "42 (5.)"
     * */
    value: string
    /**
     * callback triggered pressing competitor or competition logo (optional)
     * favorite logo cannot be pressed (makes no sense going from team page to team page)
     * */
    onPress?: () => void
}

export type StatisticsRowProps = {
    /**
     * determines which "slot" to highlight and how far the "progress bar" goes
     * */
    favoritePosition: 'LAST' | 'INBETWEEN' | 'FIRST'
    /**
     * what statistic are we showing, e.g. "Tore"
     * */
    category: string
    /**
     * last competitor on the left
     * */
    last: DataPoint
    /**
     * first competitor on the right
     * */
    first: DataPoint
    /**
     * in-between competitor somewhere in the middle (optional)
     * e.g. penultimate, favorite or runnerup
     * */
    inBetween?: DataPoint
    /**
     * number between 0 and 100 that determines x position of in-between competitor (optional - defaults to 50)
     * */
    percentage?: number
    /**
     * competition with average value to show in the middle (optional)
     * */
    average?: DataPoint
} & TestID

const StatisticsRow: FunctionComponent<StatisticsRowProps & ThemeProps> = ({
    favoritePosition,
    category,
    last,
    first,
    inBetween,
    percentage = 50,
    average,
    testID,
    theme = lightTheme,
}) => {
    const isLast = favoritePosition === 'LAST'
    const isInBetween = favoritePosition === 'INBETWEEN'
    const isFirst = favoritePosition === 'FIRST'
    // Limit to values interpolated between 15 and 85
    const cappedPercentage = Math.max(Math.min(percentage, 100), 0) * 0.7 + 15
    let progressBarWidth
    if (isLast) progressBarWidth = 0
    if (isInBetween) progressBarWidth = cappedPercentage
    if (isFirst) progressBarWidth = 100

    return (
        <Grid noExpand height={70} paddingX={2} backgroundColor="pearl" testID={testID}>
            {/* title row */}
            <Grid noExpand height={14} centerHorizontal marginBottom={2}>
                <CaptionFont color="onyx">{category}</CaptionFont>
            </Grid>
            {/* graphics/logo row */}
            <Grid noExpand horizontal center height={24}>
                <Touchable onPress={isLast ? undefined : last.onPress}>
                    {/* bigger touch target */}
                    <Grid noExpand center minWidth={30} minHeight={44}>
                        <Grid
                            position="absolute"
                            noExpand
                            center
                            width={isLast ? 24 : 16}
                            height={isLast ? 24 : 16}
                            zIndex={10}
                            backgroundColor="pearl"
                            borderRadius={isLast ? 12 : 8}
                            opacity={isLast ? 1 : 0.7}
                        >
                            <Image
                                imageUrl={last.logoUrl}
                                aspectRatio="1:1"
                                width={isLast ? 20 : 12}
                                size="S"
                            />
                        </Grid>
                    </Grid>
                </Touchable>
                <Grid centerVertical>
                    {/* this grid creates the gray bar behind the logos and serves as an anker for positioning */}
                    <Grid position="relative" noExpand height={2} center backgroundColor="moon-3">
                        {inBetween ? (
                            <Grid
                                center
                                position="absolute"
                                left={`${cappedPercentage}%`}
                                zIndex={20}
                            >
                                <Touchable
                                    disableHover
                                    onPress={isInBetween ? undefined : inBetween.onPress}
                                >
                                    {/* bigger touch target */}
                                    <Grid
                                        position="absolute"
                                        center
                                        minHeight={44}
                                        minWidth={40}
                                        left={-20}
                                        top={-22}
                                    >
                                        <Grid
                                            position="absolute"
                                            left={isInBetween ? -12 + 20 : -8 + 20}
                                            center
                                            width={isInBetween ? 24 : 16}
                                            height={isInBetween ? 24 : 16}
                                            backgroundColor="pearl"
                                            borderRadius={isInBetween ? 12 : 8}
                                        >
                                            {/* extra grid for opacity so bar does not shine through */}
                                            <Grid center opacity={isInBetween ? 1 : 0.7}>
                                                <Image
                                                    imageUrl={inBetween.logoUrl}
                                                    aspectRatio="1:1"
                                                    width={isInBetween ? 20 : 12}
                                                    size="S"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Touchable>
                            </Grid>
                        ) : null}
                        {average ? (
                            <Grid
                                position="absolute"
                                noExpand
                                center
                                width={16}
                                height={16}
                                zIndex={10}
                                backgroundColor="pearl"
                                borderRadius={8}
                            >
                                <Touchable onPress={average.onPress}>
                                    {/* bigger touch target */}
                                    <Grid center width={44} minHeight={44} opacity={0.7}>
                                        <Image
                                            imageUrl={average.logoUrl}
                                            aspectRatio="1:1"
                                            width={12}
                                            size="S"
                                        />
                                    </Grid>
                                </Touchable>
                            </Grid>
                        ) : null}
                        <Grid
                            noExpand
                            position="absolute"
                            left={0}
                            minHeight={4}
                            width={`${progressBarWidth}%`}
                            backgroundColor="onyx"
                        />
                    </Grid>
                </Grid>
                <Touchable onPress={isFirst ? undefined : first.onPress}>
                    {/* bigger touch target */}
                    <Grid noExpand center minWidth={30} minHeight={44}>
                        <Grid
                            position="absolute"
                            noExpand
                            center
                            width={isFirst ? 24 : 16}
                            height={isFirst ? 24 : 16}
                            zIndex={10}
                            backgroundColor="pearl"
                            borderRadius={isFirst ? 12 : 8}
                            opacity={isFirst ? 1 : 0.7}
                        >
                            <Image
                                imageUrl={first.logoUrl}
                                aspectRatio="1:1"
                                width={isFirst ? 20 : 12}
                                size="S"
                            />
                        </Grid>
                    </Grid>
                </Touchable>
            </Grid>
            {/* value/label row */}
            <Grid position="relative" noExpand horizontal height={12}>
                {inBetween ? (
                    <Grid horizontal position="absolute" width="100%" paddingX={30} zIndex={10}>
                        <Grid position="relative">
                            <Touchable
                                onPress={isInBetween ? undefined : inBetween.onPress}
                                disableHover
                            >
                                <Grid horizontal position="absolute" left={`${cappedPercentage}%`}>
                                    <Grid
                                        position="absolute"
                                        left={-42}
                                        width={12}
                                        height={12}
                                        rotate={90}
                                    >
                                        {/* gradient on both sides to "slide" over average label gently */}
                                        <Gradient
                                            expand
                                            gradient={[
                                                theme?.colors.pearl || 'rgba(255, 255, 255, 0)',
                                                theme?.colors.TRANSPARENT ||
                                                    'rgba(255, 255, 255, 1)',
                                            ]}
                                        />
                                    </Grid>
                                    <Grid
                                        position="absolute"
                                        left={-30}
                                        width={60}
                                        height={12}
                                        backgroundColor="pearl"
                                    >
                                        <Label10Font
                                            textAlign="center"
                                            bold={isInBetween}
                                            color={isInBetween ? 'onyx' : 'keshi-3'}
                                        >
                                            {inBetween.value}
                                        </Label10Font>
                                    </Grid>
                                    <Grid
                                        position="absolute"
                                        left={30}
                                        width={12}
                                        height={12}
                                        rotate={90}
                                    >
                                        <Gradient
                                            expand
                                            gradient={[
                                                theme?.colors.TRANSPARENT ||
                                                    'rgba(255, 255, 255, 1)',
                                                theme?.colors.pearl || 'rgba(255, 255, 255, 0)',
                                            ]}
                                        />
                                    </Grid>
                                </Grid>
                            </Touchable>
                        </Grid>
                    </Grid>
                ) : null}

                <Touchable onPress={isLast ? undefined : last.onPress} disableHover>
                    <Grid width={30}>
                        <Label10Font
                            textAlign="center"
                            bold={isLast}
                            color={isLast ? 'onyx' : 'keshi-3'}
                        >
                            {last.value}
                        </Label10Font>
                    </Grid>
                </Touchable>

                <Grid center>
                    {average ? (
                        <Touchable onPress={average.onPress} disableHover>
                            <Grid noExpand center width={30}>
                                <Label10Font textAlign="center" color="keshi-3">
                                    {average.value}
                                </Label10Font>
                            </Grid>
                        </Touchable>
                    ) : null}
                </Grid>

                <Touchable onPress={isFirst ? undefined : first.onPress} disableHover>
                    <Grid width={30}>
                        <Label10Font
                            textAlign="center"
                            bold={isFirst}
                            color={isFirst ? 'onyx' : 'keshi-3'}
                        >
                            {first.value}
                        </Label10Font>
                    </Grid>
                </Touchable>
            </Grid>
        </Grid>
    )
}

export default withTheme(StatisticsRow) as FunctionComponent<StatisticsRowProps>
